import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Save, Copy, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import AiGenerateDialog from "./AiGenerateDialog";

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snacks"];
const emptyMeal = { items: [], alternatives: [], notes: "" };

function emptyDay(n) {
  return {
    day_number: n,
    label: `Day ${n}`,
    meals: { breakfast: { ...emptyMeal }, lunch: { ...emptyMeal }, dinner: { ...emptyMeal }, snacks: { ...emptyMeal } }
  };
}

const initial = {
  patient_id: "",
  name: "",
  plan_type: "detailed",
  days: [emptyDay(1)],
  general_structure: { breakfast: "", lunch: "", dinner: "", snacks: "" },
  notes: "",
  active: true,
  start_date: new Date().toISOString().slice(0, 10),
};

export default function DietPlanForm() {
  const { id } = useParams();
  const editing = !!id;
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...initial, patient_id: params.get("patient") || "" });
  const [patients, setPatients] = useState([]);
  const [foods, setFoods] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [basedOn, setBasedOn] = useState(null); // { plan_id, name, patient_name }
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/patients"), api.get("/foods"), api.get("/diet-plans")])
      .then(([{ data: pts }, { data: fds }, { data: pls }]) => {
        setPatients(pts);
        setFoods(fds);
        setTemplates(pls);
      })
      .catch((e) => toast.error(formatApiError(e)));
    if (editing) {
      api.get(`/diet-plans/${id}`).then(({ data }) => {
        setForm({ ...initial, ...data, general_structure: data.general_structure || initial.general_structure });
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [editing, id]);

  const loadTemplate = (sourcePlan) => {
    // Deep clone the meal structure so edits on the new plan don't mutate the source
    const cloned = JSON.parse(JSON.stringify({
      plan_type: sourcePlan.plan_type || "detailed",
      days: sourcePlan.days || [emptyDay(1)],
      general_structure: sourcePlan.general_structure || initial.general_structure,
      notes: sourcePlan.notes || "",
    }));
    setForm((p) => ({
      ...p,
      // Preserve current patient_id & name (dietitian should retarget) — only fill if blank
      patient_id: p.patient_id || "",
      name: p.name || `${sourcePlan.name} (copy)`,
      plan_type: cloned.plan_type,
      days: cloned.days.length ? cloned.days : [emptyDay(1)],
      general_structure: cloned.general_structure,
      notes: cloned.notes,
      active: true,
      start_date: new Date().toISOString().slice(0, 10),
    }));
    const patient = patients.find((x) => x.user_id === sourcePlan.patient_id);
    setBasedOn({ plan_id: sourcePlan.plan_id, name: sourcePlan.name, patient_name: patient?.name || null });
    setTemplateDialogOpen(false);
    toast.success(`Loaded "${sourcePlan.name}" — customize and save as new`);
  };

  const clearTemplate = () => setBasedOn(null);

  const applyAiPlan = (aiPlan) => {
    if (!aiPlan) return;
    const cloned = JSON.parse(JSON.stringify(aiPlan));
    setForm((p) => ({
      ...p,
      // Keep current patient selection + start date + active toggle
      name: p.name || cloned.name || "AI-generated plan",
      plan_type: cloned.plan_type || p.plan_type,
      days: cloned.plan_type === "detailed" ? (cloned.days?.length ? cloned.days : [emptyDay(1)]) : p.days,
      general_structure: cloned.plan_type === "general" ? cloned.general_structure : p.general_structure,
      notes: cloned.notes || p.notes,
    }));
    setBasedOn({ plan_id: "ai", name: cloned.name || "AI-generated draft", patient_name: null, kind: "ai" });
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addDay = () => set("days", [...form.days, emptyDay(form.days.length + 1)]);
  const removeDay = (idx) => {
    const arr = form.days.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day_number: i + 1, label: `Day ${i + 1}` }));
    set("days", arr);
  };
  const updateMeal = (dayIdx, mealKey, updater) => {
    const days = [...form.days];
    days[dayIdx] = { ...days[dayIdx], meals: { ...days[dayIdx].meals, [mealKey]: updater(days[dayIdx].meals[mealKey] || { ...emptyMeal }) } };
    set("days", days);
  };
  const addItem = (di, mk) => updateMeal(di, mk, (m) => ({ ...m, items: [...(m.items || []), { food_id: "", name: "", qty: 1, unit: "", notes: "" }] }));
  const updItem = (di, mk, ii, k, v) => updateMeal(di, mk, (m) => {
    const items = [...m.items];
    items[ii] = { ...items[ii], [k]: v };
    if (k === "food_id") {
      const f = foods.find((x) => x.food_id === v);
      if (f) { items[ii].name = f.name; items[ii].unit = f.unit; }
    }
    return { ...m, items };
  });
  const removeItem = (di, mk, ii) => updateMeal(di, mk, (m) => ({ ...m, items: m.items.filter((_, i) => i !== ii) }));

  const save = async () => {
    if (!form.patient_id) { toast.error("Select a patient"); return; }
    if (!form.name) { toast.error("Plan name required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        days: form.days.map((d) => ({
          ...d,
          meals: Object.fromEntries(Object.entries(d.meals).map(([k, m]) => [k, {
            ...m,
            items: (m.items || []).map((it) => ({ ...it, qty: it.qty === "" ? null : Number(it.qty) })),
            alternatives: m.alternatives || [],
          }]))
        })),
      };
      if (editing) await api.put(`/diet-plans/${id}`, payload);
      else await api.post(`/diet-plans`, payload);
      toast.success(editing ? "Plan updated" : "Plan created");
      navigate("/diet-plans");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="diet-plan-form-page">
      <Link to="/diet-plans" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-plans">
        <ArrowLeft size={16} /> Back to plans
      </Link>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold">{editing ? "Edit diet plan" : "New diet plan"}</h1>
        {!editing && (
          <div className="flex items-center gap-2 flex-wrap">
            <AiGenerateDialog
              patientId={form.patient_id}
              planType={form.plan_type}
              onGenerated={applyAiPlan}
              disabled={!form.patient_id}
            />
            <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" className="gap-2 rounded-full" data-testid="open-template-picker">
                <Sparkles size={14} /> Start from existing plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="template-picker-dialog">
              <DialogHeader>
                <DialogTitle>Choose a plan as template</DialogTitle>
                <DialogDescription>
                  Pick any plan you've created before. Its meals will be copied into this new plan — you can customize and save as a new plan for a different patient.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1">
                {templates.length === 0 ? (
                  <div className="p-10 text-center text-sm text-muted-foreground" data-testid="no-templates">
                    No previous plans yet — once you save your first plan, it will appear here.
                  </div>
                ) : (
                  <ul className="divide-y divide-border" data-testid="template-list">
                    {templates.map((tpl) => {
                      const patient = patients.find((x) => x.user_id === tpl.patient_id);
                      const dayCount = (tpl.days || []).length;
                      return (
                        <li key={tpl.plan_id}>
                          <button
                            type="button"
                            onClick={() => loadTemplate(tpl)}
                            className="w-full text-left py-3 px-2 hover:bg-muted/60 rounded-lg flex items-start justify-between gap-3"
                            data-testid={`template-item-${tpl.plan_id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-sm truncate">{tpl.name}</div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {tpl.plan_type === "general" ? "General structure" : `${dayCount} day${dayCount !== 1 ? "s" : ""}`}
                                {patient ? ` • originally for ${patient.name}` : ""}
                                {tpl.created_at ? ` • ${new Date(tpl.created_at).toLocaleDateString()}` : ""}
                              </div>
                            </div>
                            <span className="inline-flex items-center gap-1 text-xs text-primary shrink-0 mt-1">
                              <Copy size={12} /> Use
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {basedOn && (
        <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-primary/10 text-primary w-fit" data-testid="based-on-badge">
          <Sparkles size={12} />
          <span>
            {basedOn.kind === "ai"
              ? <>AI draft: <b>{basedOn.name}</b> — review and customize</>
              : <>Based on <b>{basedOn.name}</b>{basedOn.patient_name ? ` (from ${basedOn.patient_name})` : ""}</>
            }
          </span>
          <button type="button" onClick={clearTemplate} className="ml-1 hover:opacity-70" aria-label="Clear template reference" data-testid="clear-template-reference">
            <X size={12} />
          </button>
        </div>
      )}

      <div className="nt-card p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Patient</Label>
            <select value={form.patient_id} onChange={(e) => set("patient_id", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="plan-patient-select">
              <option value="">— Select patient —</option>
              {patients.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Plan name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Weight-loss week 1" data-testid="plan-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Start date</Label>
            <Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="plan-start-date" />
          </div>
          <div className="space-y-2 flex items-center justify-between pt-6">
            <Label>Active plan</Label>
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="plan-active-switch" />
          </div>
        </div>

        <Tabs value={form.plan_type} onValueChange={(v) => set("plan_type", v)}>
          <TabsList data-testid="plan-type-tabs">
            <TabsTrigger value="detailed" data-testid="tab-detailed">Detailed daily plan</TabsTrigger>
            <TabsTrigger value="general" data-testid="tab-general">General structure</TabsTrigger>
          </TabsList>

          <TabsContent value="detailed" className="mt-4 space-y-5">
            {form.days.map((day, di) => (
              <div key={di} className="border border-border rounded-xl p-5 space-y-4" data-testid={`day-${di}`}>
                <div className="flex items-center justify-between">
                  <Input value={day.label || ""} onChange={(e) => { const ds = [...form.days]; ds[di] = { ...ds[di], label: e.target.value }; set("days", ds); }} className="max-w-xs font-semibold" data-testid={`day-label-${di}`} />
                  {form.days.length > 1 && (
                    <button onClick={() => removeDay(di)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-day-${di}`}><Trash2 size={14} /></button>
                  )}
                </div>
                {MEAL_TYPES.map((mk) => {
                  const meal = day.meals[mk] || emptyMeal;
                  return (
                    <div key={mk} className="bg-muted/40 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="capitalize font-semibold">{mk}</div>
                        <Button type="button" variant="outline" size="sm" onClick={() => addItem(di, mk)} className="gap-1" data-testid={`add-item-${di}-${mk}`}><Plus size={14} /> Item</Button>
                      </div>
                      <div className="mt-3 space-y-2">
                        {(meal.items || []).map((it, ii) => (
                          <div key={ii} className="grid grid-cols-12 gap-2 items-center">
                            <select value={it.food_id || ""} onChange={(e) => updItem(di, mk, ii, "food_id", e.target.value)} className="col-span-5 h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid={`item-food-${di}-${mk}-${ii}`}>
                              <option value="">— Food —</option>
                              {foods.map((f) => <option key={f.food_id} value={f.food_id}>{f.name}</option>)}
                            </select>
                            <Input className="col-span-2 h-9" type="number" placeholder="Qty" value={it.qty ?? ""} onChange={(e) => updItem(di, mk, ii, "qty", e.target.value)} data-testid={`item-qty-${di}-${mk}-${ii}`} />
                            <Input className="col-span-2 h-9" placeholder="Unit" value={it.unit || ""} onChange={(e) => updItem(di, mk, ii, "unit", e.target.value)} data-testid={`item-unit-${di}-${mk}-${ii}`} />
                            <Input className="col-span-2 h-9" placeholder="Notes" value={it.notes || ""} onChange={(e) => updItem(di, mk, ii, "notes", e.target.value)} />
                            <button onClick={() => removeItem(di, mk, ii)} className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" data-testid={`remove-item-${di}-${mk}-${ii}`}><Trash2 size={14} /></button>
                          </div>
                        ))}
                        <Textarea rows={2} placeholder="Meal notes / alternatives" value={meal.notes || ""} onChange={(e) => updateMeal(di, mk, (m) => ({ ...m, notes: e.target.value }))} data-testid={`meal-notes-${di}-${mk}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addDay} className="gap-2" data-testid="add-day-button"><Plus size={16} /> Add day</Button>
          </TabsContent>

          <TabsContent value="general" className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">A flexible structure (e.g., "Protein source + healthy carb + fruit").</p>
            {MEAL_TYPES.map((mk) => (
              <div key={mk} className="space-y-2">
                <Label className="capitalize">{mk}</Label>
                <Textarea
                  rows={2}
                  value={form.general_structure?.[mk] || ""}
                  onChange={(e) => set("general_structure", { ...form.general_structure, [mk]: e.target.value })}
                  data-testid={`general-${mk}`}
                />
              </div>
            ))}
          </TabsContent>
        </Tabs>

        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="plan-notes-input" />
        </div>

        <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-plan-button"><Save size={16} /> {saving ? "Saving…" : "Save plan"}</Button>
      </div>
    </div>
  );
}
